import React, { useMemo } from 'react';
import { Typography, Box } from '@material-ui/core';
import { getClassifiedClass } from '../../../utils';
import { LabelType, Defect, AnnotationBase } from '@clef/shared/types';
import cx from 'classnames';
import { useLabelingStyles } from '../../Labeling/labelingStyles';
import { SuccessIcon, ErrorIcon } from '@/images/data-browser/icons';
import { getAnomalyDetectionPredictionAnnotations } from '@clef/shared/utils';

interface AnomalyDetectionLabelPreviewListProps {
  annotations?: AnnotationBase[];
  allDefects: Defect[];
  labelType: LabelType | null | undefined;
  threshold?: number;
}

export const AnomalyDetectionLabelPreviewList: React.FC<AnomalyDetectionLabelPreviewListProps> = ({
  annotations = [],
  allDefects = [],
  labelType,
  threshold,
}) => {
  const annotation = getAnomalyDetectionPredictionAnnotations(annotations, threshold ?? 0)?.[0];
  const styles = useLabelingStyles();
  const defect = allDefects.find(defect => defect.id === annotation?.defectId);

  const classifiedClass = useMemo(() => {
    return getClassifiedClass(annotations || [], allDefects!);
  }, [allDefects, annotations]);

  const gtClassifiedClass = useMemo(() => {
    return getClassifiedClass(annotations || [], allDefects!);
  }, [allDefects, annotations]);

  const statusIndicator = useMemo((): {
    el: React.ReactNode;
    type: 'question' | 'success' | 'error';
  } | null => {
    if (labelType !== LabelType.AnomalyDetection) return null;
    if (!classifiedClass?.id) return null;

    if (!gtClassifiedClass?.id) {
      return {
        el: <div className={styles.question}>?</div>,
        type: 'question',
      };
    }
    if (classifiedClass?.id === gtClassifiedClass?.id) {
      return {
        el: <SuccessIcon />,
        type: 'success',
      };
    }
    return {
      el: <ErrorIcon />,
      type: 'error',
    };
  }, [classifiedClass?.id, gtClassifiedClass?.id, labelType, styles.question]);

  return (
    <Box display="flex">
      <Box
        display="flex"
        className={cx(
          styles.classificationPreviewBadge,
          defect?.name ? 'prediction' : 'no-prediction',
        )}
      >
        <Typography
          variant="body2"
          component="div"
          className={styles.classificationName}
          style={{
            marginLeft: statusIndicator ? 8 : 0,
            marginRight: 8,
          }}
        >
          {defect?.name ?? t('Not predicted')}
        </Typography>
      </Box>
    </Box>
  );
};
